'use client';
import { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import dynamic from 'next/dynamic';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthXs, imageFullWidthSmAndUp } from '@repo/ui/src/styles/variables';
import { HorizontalAlignmentEnum, VerticalAlignmentEnum } from '@repo/ui/src/enums/shared-enums';
import { AlignH, AlignV, ImageProps, YoutubeLinkProps } from '@repo/ui/src/types';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { InnerToutContentContainer, ToutContentCardContainer } from './_toutContentCardStyles';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';

const VideoRendered = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'), {
  ssr: false,
});

interface Props {
  toutContent: any;
  youtubeLink?: YoutubeLinkProps;
  cardBackground?: ImageProps;
  horizontalAlignment?: AlignH;
  verticalAlignment?: AlignV;
  fullWidthAndHeight?: boolean;
  noDivider?: boolean;
  className?: string;
}

export const ToutContentCard = ({
  toutContent,
  youtubeLink,
  cardBackground,
  verticalAlignment = VerticalAlignmentEnum.Top,
  horizontalAlignment = HorizontalAlignmentEnum.Left,
  fullWidthAndHeight,
  noDivider,
  className = '',
}: Props) => {
  const xsOnly = useMediaQuery((theme: any) => theme.breakpoints.only('xs'));
  const smAndUp = useMediaQuery((theme: any) => theme.breakpoints.up('sm'));
  const imgUrl = cardBackground
    ? buildImageSrc(
        cardBackground,
        smAndUp ? imageFullWidthSmAndUp : imageFullWidthXs,
        smAndUp ? 5 : 3,
        smAndUp ? 3 : 5,
      )
    : null;

  const { width = 1200 } = useWindowSize();
  
  const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';

  let isVideo;
  let vidWidth;

  if (cardBackground?._meta.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    vidWidth = width > 1280 ? 1280 : width < 600 ? (width * 140 * 16) / 9 : width;
    if (!vidWidth) {
      // Won't have width on server side, so use ssr media query to get placeholder image width
      vidWidth = xsOnly ? 600 : width * 0.8;
    }
  }

  const centerGradient =
    verticalAlignment === VerticalAlignmentEnum.Center &&
    horizontalAlignment === HorizontalAlignmentEnum.Center;
  const gradientDirection = `${
    verticalAlignment === VerticalAlignmentEnum.Center ? '' : verticalAlignment || 'left'
  } ${horizontalAlignment === HorizontalAlignmentEnum.Center ? '' : horizontalAlignment || 'top'}`;

  const [showVideoDialog, setShowVideoDialog] = useState(false);

  const onContainerClick = () => {
    if (!youtubeLink || showVideoDialog) return;
    setShowVideoDialog(true);
  };

  return (
    <ToutContentCardContainer
      className={`${className} ${youtubeLink ? 'tw-cursor-pointer' : ''}`}
      onClick={onContainerClick}
      onKeyDown={onContainerClick}
      imgUrl={imgUrl}
    >
      {isVideo && cardBackground && vidWidth && (
        <VideoRendered video={cardBackground} width={vidWidth} fillContainer className="video" />
      )}
      <InnerToutContentContainer
        centerGradient={centerGradient}
        fullWidthAndHeight={fullWidthAndHeight}
        gradientDirection={gradientDirection}
        horizontalAlignment={horizontalAlignment}
        textAlign={toutContent.align || 'left'}
        verticalAlignment={verticalAlignment}
        imgUrl={imgUrl}
      >
        <ToutContent
          dark
          divider={!noDivider}
          lineClamp={4}
          mb={noDivider ? 0 : 3}
          align={toutContent.align || 'left'}
          {...toutContent}
          youtubeLink={youtubeLink}
          showVideoDialog={showVideoDialog}
          setShowVideoDialog={setShowVideoDialog}
        />
      </InnerToutContentContainer>
    </ToutContentCardContainer>
  );
};